<template>
	<div class="width-100">
		<div class="flex-space-between--center flex-wrap pb-1 width-100">
			<div class="flex flex-wrap flex-col-8">
				<div class="dropdown-title">Connect your account</div>
				<div class="dropdown-secondary-title" v-if="secondaryTitle">{{ secondaryTitle }}</div>
			</div>
			<div
				:class="[{ 'normal-opacity': showConnectionOptions, disabled: !hasEditPermissions }]"
				class="connection-options-link"
				@click="toggleShowConnectionOptions"
				v-if="isConfigurationsOn"
			>
				{{ `${showConnectionOptions ? 'Hide ' : ''}Connection options` }}
			</div>
		</div>
		<div class="connection-options" :class="{ show: showConnectionOptions }">
			<ProfileConnectionOptions
				:serviceId="serviceId"
				:profileId="profileId"
				includeVirtualProfiles
				:contextMode="contextMode"
				:widgetFieldMode="widgetFieldMode"
				:narrowLayout="narrowLayout"
				@select-profile="selectProfile"
				:credentialsType="credentialsType"
				:credentialsData="credentialsData"
				:serviceVersionId="serviceVersionId"
				@set-testing-profile="setTestingProfile"
				:overrideSelectProfile="overrideSelectProfile"
				:disableRealProfiles="editWorkflowHasInstallable"
				:configurationFieldProps="configurationFieldProps"
			/>
		</div>
		<div class="connection-options" :class="{ show: !showConnectionOptions }">
			<ProfileSelector
				:serviceId="serviceId"
				:profileId="profileId"
				includeVirtualProfiles
				@select-profile="selectProfile"
				:credentialsType="credentialsType"
				:serviceVersionId="serviceVersionId"
				:showLoadingStatus="showLoadingStatus"
				@set-testing-profile="setTestingProfile"
				:showLockedIndication="showLockedIndication"
				:disableRealProfiles="editWorkflowHasInstallable"
				:configurationFieldProps="configurationFieldProps"
				:readonly="!hasEditPermissions"
			/>
		</div>
	</div>
</template>

<script>
/* eslint-disable prefer-const */
import { mapActions, mapGetters } from 'vuex';
import ProfileSelector from '@/components/common/ProfileSelector';
import { isConfigurationsOn } from '@/store/modules/featureFlags/getters';
import { editWorkflowHasInstallable } from '@/store/modules/selectedWorkflow/getters';
import { setParamInEditNode, setTestingProfile } from '@/store/modules/selectedNode/actions';
import ProfileConnectionOptions from '@/components/common/ProfileSelector/ProfileConnectionOptions.vue';
import { userSpecificConfigurationId, generalConfigurationId } from '@/store/modules/selectedWorkflow/modules/configurations/getters';
import { VIRTUAL_PROFILES_IDS, PER_USER_CONNECTION_ID, EMBEDDED_CONNECTION_ID } from '@/utils/credentials-service.const';
import { setContextualDataProfileID } from '@/store/modules/selectedWorkflow/actions';
import { CredentialsTypes } from '@ply-io/ply-front-shared-utilities';
export default {
	name: 'ProfileSelectionMenu',
	props: {
		contextMode: { type: Boolean },
		showLockedIndication: { type: Boolean },
		widgetFieldMode: { type: Boolean },
		narrowLayout: { type: Boolean },
		credentialsType: { type: String },
		credentialsData: { type: Object },
		serviceId: { type: [String, Number] },
		profileId: { type: [String, Number] },
		overrideSelectProfile: { type: Function },
		sourceFieldUUID: { type: [String, Number] },
		configurationId: { type: [String, Number] },
		serviceVersionId: { type: [String, Number] },
		showLoadingStatus: { type: Boolean, default: true },
		secondaryTitle: { type: String },
		hasEditPermissions: { type: Boolean },
	},
	components: {
		ProfileSelector,
		ProfileConnectionOptions,
	},
	computed: {
		...mapGetters([
			generalConfigurationId.fullPath,
			editWorkflowHasInstallable.fullPath,
			userSpecificConfigurationId.fullPath,
			isConfigurationsOn.fullPath,
		]),
		...mapGetters('selectedNode', ['editNode']),
		configurationFieldProps() {
			return {
				fieldUUID: this.sourceFieldUUID,
				configurationId: this.configurationId,
			};
		},
		editWorkflowHasInstallable() {
			return this[editWorkflowHasInstallable.fullPath];
		},
		isConfigurationsOn() {
			return this[isConfigurationsOn.fullPath];
		},
	},
	methods: {
		...mapActions([setParamInEditNode.fullPath, setTestingProfile.fullPath, setContextualDataProfileID.fullPath]),
		updateNodeKey(key, value) {
			this[setParamInEditNode.fullPath]({ key, value });
		},
		selectProfile(credsId) {
			if (!this.contextMode) {
				const credentialsType = credsId === PER_USER_CONNECTION_ID ? CredentialsTypes.ProfileBased : CredentialsTypes.AccountBased;

				if (this.overrideSelectProfile) {
					return this.overrideSelectProfile(credsId, credsId ? null : this.sourceFieldUUID, credentialsType);
				}
				if (VIRTUAL_PROFILES_IDS.includes(credsId)) {
					this.updateNodeKey('credentials', null); // clear old embedded credentials relation
					this.updateNodeKey('credentials_type', credentialsType);
				} else {
					this.updateNodeKey('credentials_type', CredentialsTypes.Embedded);
					if (credsId === EMBEDDED_CONNECTION_ID) {
						this.updateNodeKey('credentials', null);
					} else {
						this.updateNodeKey('credentials', credsId);
					}
				}
			} else {
				this.updateContextMode(credsId);
			}
		},
		updateContextMode(profileID) {
			// when ProfileSelectionMenu is rendered in actionContextNode of workflow
			let credentials = null;
			let source_field_uuid = null;
			let create_new_source_field = false;
			let credentials_type = CredentialsTypes.Embedded;

			if (VIRTUAL_PROFILES_IDS.includes(profileID)) {
				const credentialsType = profileID === PER_USER_CONNECTION_ID ? CredentialsTypes.ProfileBased : CredentialsTypes.AccountBased;
				credentials_type = credentialsType;
			} else {
				credentials_type = CredentialsTypes.Embedded;
				if (profileID === EMBEDDED_CONNECTION_ID) {
					credentials = null;
				} else {
					credentials = profileID;
				}
			}
			const credentials_configuration = {
				credentials,
				source_field_uuid,
				credentials_type,
				create_new_source_field,
			};
			this[setContextualDataProfileID.fullPath]({ credentials_configuration });
		},
		setTestingProfile(value) {
			this[setTestingProfile.fullPath](value);
		},
		toggleShowConnectionOptions() {
			if (this.hasEditPermissions) {
				this.showConnectionOptions = !this.showConnectionOptions;
			}
		},
	},
	mounted() {
		if (this.credentialsType && this.credentialsType !== CredentialsTypes.Embedded) {
			this.toggleShowConnectionOptions();
		}
	},
	data() {
		return {
			showConnectionOptions: false,
		};
	},
};
</script>

<style scoped lang="scss">
.dropdown-title {
	font-size: 12px;
	font-weight: normal;
	flex: 0 0 100%;
}
.dropdown-secondary-title {
	font-size: 10px;
	font-weight: 200;
	flex: 0 0 100%;
}
.connection-options-link {
	opacity: 0.5;
	&.normal-opacity {
		opacity: 1;
	}
	font-size: 12px;
	&:not(.disabled) {
		cursor: pointer;
	}
	letter-spacing: 0px;
	text-decoration: underline;
	color: var(--profile-selection-grey);
}
.connection-options {
	max-height: 0;
	overflow: hidden;
	opacity: 0;
	transition: opacity 0.1s linear 0s;

	&.show {
		max-height: 100%;
		opacity: 1;
		transition: opacity 0.3s linear 0.1s;
	}
}
</style>
